/* eslint-disable max-lines */
import { type JSX, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PromptLibraryItem } from "@/api/models"
import type { PersonalSearchControl } from "@/features/ai-explorer/hooks/use-personal-search"
import type { TranslatorToolControl } from "@/features/ai-translator/translator-tool-utils"
import type { SessionType } from "@/types"

import {
  AudioLines,
  BookOpen,
  ChevronLeft,
  Globe,
  Languages,
  LanguagesSolid,
  Library,
  LifeBuoy,
  Lock,
  Mail,
  Network,
  Orbit,
  Plug,
  Server,
  ShieldCheck,
  Sparkles,
  Telescope,
  Wrench,
  X,
} from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { NodePill } from "@/components/ui/node-pill"
import { M365Icon } from "@/features/ai-explorer/components/m365-icon"
import { TranslatorToolPanel } from "@/features/ai-translator/components/translator-tool-panel"
import { useVoiceEngine } from "@/features/chat/hooks/use-voice-engine"
import { LibraryPicker } from "@/features/navigator/components/library-picker"
import { useLibrariesSelection } from "@/features/navigator/hooks/use-libraries-selection"
import { type PluginCatalogState, usePluginCatalog } from "@/features/plugins/use-plugin-catalog"
import { TrustedSourcesDialog } from "@/features/trusted-sources/components/trusted-sources-dialog"
import { TrustedSourcesPanel } from "@/features/trusted-sources/components/trusted-sources-panel"
import { useTrustedSourcesSelection } from "@/features/trusted-sources/hooks/use-trusted-sources-selection"
import { useFeatureEnabled } from "@/hooks/use-feature-enabled"
import { useIsMobile } from "@/hooks/use-mobile"
import { useDeepExplorerAvailability } from "@/hooks/use-route-overrides"
import { cn } from "@/lib/utils"

import { PersonalSearchSourceRows } from "./personal-search-source-rows"
import { MenuToggleRow, PluginsMenuPanel } from "./plugins-menu-panel"
import { PromptLibrariesDialog } from "./prompt-libraries-dialog"
import { ToolsIcon } from "./tools-icon"
import { useGetPrompts } from "../hooks/use-get-prompts"

type ExplorerFooterToolbarProps = {
  translator?: TranslatorToolControl
  sessionType: SessionType
  showWebSearchOption: boolean
  showM365Option: boolean
  showServiceDeskOption: boolean
  showNetboxOption: boolean
  // X-One tool: optional so non-Explorer callers (tender, studio-ai, dash)
  // don't have to wire it. Only the Explorer/Deep surface passes these today.
  showXOneOption?: boolean
  enableAgent: boolean
  // Deep Explorer: optional so non-Explorer callers (tender, studio-ai) don't
  // have to wire the new toggle. Walking-skeleton phase only — phase 2 may
  // make it required if deep mode is exposed in those surfaces.
  enableDeep?: boolean
  enableWebSearch: boolean
  enableM365: boolean
  enableServiceDesk: boolean
  enableNetbox: boolean
  enableXOne?: boolean
  isStreaming: boolean
  hasAgentModeFeature: boolean
  isPromptsFeatureEnabled: boolean
  /**
   * Legacy single-toggle high-reasoning support for callers that don't use
   * the four-mode ReasoningModePill (Tender, Studio AI). Explorer renders the
   * pill in the composer footer instead and leaves this unset.
   */
  showHighReasoning?: boolean
  enableHighReasoning?: boolean
  onHighReasoningChange?: (enabled: boolean) => void
  enableTreeRetrieval?: boolean
  onTreeRetrievalChange?: (enabled: boolean) => void
  onWebSearchChange: (enabled: boolean) => void
  onM365Change: (enabled: boolean) => void
  onAgentChange: (enabled: boolean) => void
  onDeepChange?: (enabled: boolean) => void
  /** Number of in-progress todos (for active badge on Deep toggle) */
  activeTodoCount?: number
  onServiceDeskChange: (enabled: boolean) => void
  onNetboxChange: (enabled: boolean) => void
  onXOneChange?: (enabled: boolean) => void
  /**
   * Plugin store (the "Plugins" submenu). Optional: only the Explorer page
   * wires it; other callers keep the backend default (every installed plugin
   * stays available to the model).
   */
  showPluginsOption?: boolean
  enablePlugins?: boolean
  onPluginsChange?: (enabled: boolean) => void
  disabledPluginIds?: string[]
  onPluginDisabledChange?: (pluginId: string, disabled: boolean) => void
  /** Receives the installed plugin ids whenever the catalog (re)loads. */
  onInstalledPluginsChange?: (pluginIds: string[]) => void
  /** Opens the /plugins page; omitted when that route is not enabled. */
  onManagePlugins?: () => void
  onStartVoiceMode?: () => void
  onPromptClicked?: (prompt: PromptLibraryItem) => void
  hideVoiceMode?: boolean
  /**
   * Search of the user's own mail, files and chats (PS-5). Optional: only the Explorer page
   * wires it, and the row appears only when the user has a source switched on.
   */
  personalSearch?: PersonalSearchControl
}

type ToolToggleItemProps = {
  icon: JSX.Element
  label: string
  enabled: boolean
  onToggle: (enabled: boolean) => void
  count?: number
  /** Small pill badge shown next to label (e.g. "Beta") */
  badge?: string
  disabled?: boolean
  title?: string
  testId?: string
}

type ActiveToolIndicator = {
  id: string
  icon: JSX.Element
  label?: string
}

const EMPTY_PLUGIN_IDS: string[] = []
const EMPTY_PLUGIN_CATALOG: PluginCatalogState = {
  storeEnabled: false,
  items: [],
  installed: [],
  isPending: true,
  isError: false,
}

/**
 * Hosts the plugin catalog query and hands its state to the toolbar. Kept as
 * a child so react-query is only touched when the Plugins submenu is wired.
 */
function PluginCatalogBridge({ onChange }: { onChange: (state: PluginCatalogState) => void }) {
  const catalog = usePluginCatalog(true)
  const { storeEnabled, items, installed, isPending, isError } = catalog
  useEffect(() => {
    onChange({ storeEnabled, items, installed, isPending, isError })
  }, [onChange, storeEnabled, items, installed, isPending, isError])
  return null
}

type DrawerBackButtonProps = {
  onClick: () => void
  label: string
}

function DrawerBackButton({ onClick, label }: DrawerBackButtonProps) {
  return (
    <button
      aria-label={label}
      className="text-muted-foreground hover:text-foreground -ml-1 inline-flex h-8 w-8 items-center justify-center rounded-md"
      type="button"
      onClick={onClick}
    >
      <ChevronLeft className="size-5" />
    </button>
  )
}

// Deep Explorer is still in active development — keep the "Beta" badge pinned
// until the feature stabilises. (The Deep panel intro overlay is controlled in
// DeepExplorerSidePanel + ai-explorer-store, not localStorage.)

function ToolToggleItem({
  icon,
  label,
  enabled,
  onToggle,
  count,
  badge,
  disabled = false,
  title,
  testId,
}: ToolToggleItemProps) {
  return (
    <DropdownMenuItem
      aria-checked={enabled}
      data-testid={testId}
      disabled={disabled}
      role="menuitemcheckbox"
      title={title}
      className={cn(
        "my-0.5 gap-2",
        enabled && "text-accent-foreground dark:text-foreground dark:focus:text-accent-foreground",
        disabled && "cursor-not-allowed opacity-60"
      )}
      onSelect={(event) => {
        event.preventDefault()
        if (disabled) return
        onToggle(!enabled)
      }}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge && (
        <span className="bg-primary/10 text-primary rounded-sm px-1 py-0.5 text-[10px] leading-none font-medium">
          {badge}
        </span>
      )}
      <span className="ml-auto flex items-center gap-2">
        {typeof count === "number" && count > 0 && (
          <span className="text-muted-foreground text-xs tabular-nums">({count})</span>
        )}
        <span className="flex size-4 items-center justify-center">
          <span
            className={cn(
              "size-2 rounded-full",
              enabled ? "bg-primary" : "border-muted-foreground/50 border"
            )}
          />
        </span>
      </span>
    </DropdownMenuItem>
  )
}

export function ExplorerFooterToolbar({
  translator,
  sessionType,
  showWebSearchOption,
  showM365Option,
  showServiceDeskOption,
  showNetboxOption,
  showXOneOption = false,
  enableAgent,
  enableDeep = false,
  enableWebSearch,
  enableM365,
  enableServiceDesk,
  enableNetbox,
  enableXOne = false,
  isStreaming,
  hasAgentModeFeature,
  isPromptsFeatureEnabled,
  showHighReasoning = false,
  enableHighReasoning = false,
  onHighReasoningChange,
  enableTreeRetrieval = false,
  onTreeRetrievalChange,
  onWebSearchChange,
  onM365Change,
  onAgentChange,
  onDeepChange,
  // Deep toggle is hidden if no onDeepChange handler is wired (tender/studio-ai callers).
  activeTodoCount = 0,
  onServiceDeskChange,
  onNetboxChange,
  onXOneChange,
  showPluginsOption = false,
  enablePlugins = true,
  onPluginsChange,
  disabledPluginIds = EMPTY_PLUGIN_IDS,
  onPluginDisabledChange,
  onInstalledPluginsChange,
  onManagePlugins,
  onStartVoiceMode,
  onPromptClicked,
  hideVoiceMode = false,
  personalSearch,
}: ExplorerFooterToolbarProps) {
  const { t } = useTranslation()
  const [showTrustedSourcesDialog, setShowTrustedSourcesDialog] = useState(false)
  const [showPromptLibrariesDialog, setShowPromptLibrariesDialog] = useState(false)
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false)
  const [translatorPanelOpen, setTranslatorPanelOpen] = useState(false)
  const translatorIndicatorRef = useRef<HTMLButtonElement>(null)
  const restoreTranslatorFocus = (event: Event) => {
    if (translatorIndicatorRef.current && !translatorIndicatorRef.current.disabled) {
      event.preventDefault()
      translatorIndicatorRef.current.focus()
    }
  }
  const [librariesMenuOpen, setLibrariesMenuOpen] = useState(false)
  const [trustedSourcesMenuOpen, setTrustedSourcesMenuOpen] = useState(false)
  const [librariesDrawerOpen, setLibrariesDrawerOpen] = useState(false)
  const [promptsDrawerOpen, setPromptsDrawerOpen] = useState(false)
  const [trustedSourcesDrawerOpen, setTrustedSourcesDrawerOpen] = useState(false)
  const [toolsDrawerOpen, setToolsDrawerOpen] = useState(false)
  const [pluginsDrawerOpen, setPluginsDrawerOpen] = useState(false)
  const isMobile = useIsMobile({ breakpoint: 640, includeContentSidebarOpen: false })

  useEffect(() => {
    // Reset transient menu/drawer state when the viewport crosses the
    // breakpoint, so a Drawer↔Popover/submenu swap never leaves a surface open.
    setTrustedSourcesMenuOpen(false)
    if (!isMobile) {
      setLibrariesDrawerOpen(false)
      setPromptsDrawerOpen(false)
      setTrustedSourcesDrawerOpen(false)
      setToolsDrawerOpen(false)
      setPluginsDrawerOpen(false)
    }
  }, [isMobile])

  // Returns to the Tools menu after a drawer closes (mobile back navigation).
  const backToToolsMenu = (closeDrawer: () => void) => () => {
    closeDrawer()
    setToolsMenuOpen(true)
  }
  const isVoiceModeEnabled = useVoiceEngine() !== null

  // Plugin store: the catalog query is shared with the /plugins page, so an
  // install there shows up here without a reload. Only fetched when the
  // caller wired the submenu, so tender/studio/dash stay request-free.
  const hasPluginsMenu = showPluginsOption && typeof onPluginsChange === "function"
  // The catalog query lives in PluginCatalogBridge, mounted only while the
  // submenu is wired: the toolbar itself must stay renderable without a
  // QueryClientProvider (tender/studio callers and the toolbar unit tests).
  const [pluginCatalog, setPluginCatalog] = useState<PluginCatalogState>(EMPTY_PLUGIN_CATALOG)
  const installedPluginIds = useMemo(
    () => pluginCatalog.installed.map((entry) => entry.id),
    [pluginCatalog.installed]
  )
  // The joined key is the identity of the id list; the callback lives in a
  // ref so a new inline handler from the parent does not re-fire the effect.
  const installedPluginKey = installedPluginIds.join("\u0000")
  const onInstalledPluginsChangeRef = useRef(onInstalledPluginsChange)
  onInstalledPluginsChangeRef.current = onInstalledPluginsChange
  useEffect(() => {
    if (!hasPluginsMenu || pluginCatalog.isPending) return
    onInstalledPluginsChangeRef.current?.(
      installedPluginKey ? installedPluginKey.split("\u0000") : []
    )
  }, [hasPluginsMenu, pluginCatalog.isPending, installedPluginKey])
  const activePluginCount = enablePlugins
    ? installedPluginIds.filter((pluginId) => !disabledPluginIds.includes(pluginId)).length
    : 0
  const iconClassName = (enabled: boolean) =>
    cn("size-4", enabled ? "text-primary" : "text-muted-foreground")

  // Trusted sources selection state
  const {
    sources: trustedSources,
    personalSources,
    companySources,
    selectedUrls,
    isAllSelected,
    toggleSource,
    toggleAll,
    trustedMode,
    setTrustedMode,
  } = useTrustedSourcesSelection(sessionType)

  const { data: promptsData, isPending: isPromptsPending } = useGetPrompts()
  const prompts = promptsData?.prompts || []
  const personalPrompts = prompts.filter((p) => p.type === "personal")
  const companyPrompts = prompts.filter((p) => p.type === "company")

  const {
    hasLibraryTool,
    availableLibraries,
    activeLibraryIds,
    setActiveLibraryIds,
    isLoading: isLibrariesLoading,
    showCategoryPicker,
  } = useLibrariesSelection(sessionType)

  const shouldShowLibraries = hasLibraryTool && !isLibrariesLoading && availableLibraries.length > 0
  const shouldShowLibrariesInFooter = shouldShowLibraries && !showCategoryPicker

  // Deep Explorer is visible only when (a) the caller wires onDeepChange
  // (Explorer page does; tender/studio-ai callers do not) AND (b) the admin
  // dashboard has `explorer_deep` enabled in system features AND (c) the
  // current route doesn't set `disableDeep`. The backend WS route rejects
  // with 403 when the feature is off or the route locks Deep, so showing the
  // toggle anyway produces a confusing failure.
  //
  // A fair-use PLAN that lacks `deep` is the one case where the toggle stays
  // rendered but dead (fair use round 2, #3087): the tenant has Deep and a
  // colleague on Pro is using it, so removing the control would misrepresent
  // the product. It is disabled, lock-badged and explained instead — and
  // `useDeepExplorerEnabled` (false while locked) still keeps every send path
  // off `/ws/explorer-deep`.
  const deepAvailability = useDeepExplorerAvailability()
  const isDeepPlanLocked = deepAvailability === "locked"
  const hasDeepToggle = typeof onDeepChange === "function" && deepAvailability !== "hidden"

  // Tree Retrieval toggle follows the same admin-gate pattern as Deep
  // Explorer: visible only when (a) the caller wires onTreeRetrievalChange
  // AND (b) the admin dashboard has `tree_retrieval` enabled in system
  // features. The backend strips `library_tree_search` from the explorer
  // agent's tool list when the feature is off, so a stale-on toggle would
  // silently no-op.
  const isTreeRetrievalFeatureEnabled = useFeatureEnabled("tree_retrieval")
  const hasTreeRetrievalToggle =
    typeof onTreeRetrievalChange === "function" && isTreeRetrievalFeatureEnabled

  // Thorough search / tree retrieval is in beta — keep the badge visible in the
  // footer toggle while the feature is rolled out.
  const treeRetrievalBetaBadge = hasTreeRetrievalToggle
    ? t("deepExplorer.beta_badge", { defaultValue: "Beta" })
    : undefined

  // "Beta" badge stays pinned while Deep Explorer is in development. A plan
  // lock takes the badge slot instead: "Beta" describes the feature's
  // maturity, which is not the fact the user needs when the row is greyed out.
  const showDeepBetaBadge = hasDeepToggle && !isDeepPlanLocked
  const deepBetaBadge = showDeepBetaBadge
    ? t("deepExplorer.beta_badge", { defaultValue: "Beta" })
    : undefined
  const deepLockBadge = isDeepPlanLocked
    ? t("fairUse.plan.lockedBadge", { defaultValue: "Upgrade" })
    : undefined
  const deepLockHint = isDeepPlanLocked
    ? t("fairUse.plan.lockedHint", {
        defaultValue: "Not included in your plan. Ask your administrator to upgrade it.",
      })
    : undefined

  // Determine if we need separators based on what sections have content
  const hasModesSection = hasAgentModeFeature || showHighReasoning || hasDeepToggle
  const hasSourcesSection = shouldShowLibrariesInFooter || (!enableAgent && showWebSearchOption)
  // Built-in integrations (Microsoft 365, ServiceDesk, Netbox, X-One) are
  // grouped in a "Tools" submenu and the plugin store in a "Plugins" submenu,
  // in their own section, so the top level stays short however many of
  // them a tenant enables.
  const showXOneToggle = showXOneOption && typeof onXOneChange === "function"
  const activePersonalSearch = personalSearch?.available === true ? personalSearch : undefined
  const showPersonalSearch = activePersonalSearch !== undefined
  const personalSearchLabel = t("personalSources.search.toggle", {
    defaultValue: "Personal sources",
  })
  const personalSearchLockedHint = activePersonalSearch?.locked
    ? t("personalSources.search.lockedHint", {
        defaultValue: "Personal search is off in shared chats.",
      })
    : undefined
  const showToolsSubmenu =
    (!enableAgent && showM365Option) ||
    showServiceDeskOption ||
    showNetboxOption ||
    showXOneToggle ||
    showPersonalSearch
  const activeToolCount = [
    !enableAgent && showM365Option && enableM365,
    showServiceDeskOption && enableServiceDesk,
    showNetboxOption && enableNetbox,
    showXOneToggle && enableXOne,
    activePersonalSearch?.enabled === true,
  ].filter(Boolean).length
  const hasIntegrationsSection = showToolsSubmenu || hasPluginsMenu
  const showVoiceMenu = isVoiceModeEnabled && !hideVoiceMode
  const hasUtilitiesSection = isPromptsFeatureEnabled || showVoiceMenu

  const integrationToggleItems = (
    <>
      {!enableAgent && showM365Option && (
        <ToolToggleItem
          enabled={enableM365}
          icon={<M365Icon noText className={iconClassName(enableM365)} />}
          label={t("questioninput.m365_search")}
          onToggle={onM365Change}
        />
      )}
      {showServiceDeskOption && (
        <ToolToggleItem
          enabled={enableServiceDesk}
          icon={<LifeBuoy className={iconClassName(enableServiceDesk)} />}
          label={t("questioninput.service_desk", { defaultValue: "ServiceDesk" })}
          onToggle={onServiceDeskChange}
        />
      )}
      {showNetboxOption && (
        <ToolToggleItem
          enabled={enableNetbox}
          icon={<Server className={iconClassName(enableNetbox)} />}
          label={t("questioninput.netbox", { defaultValue: "Netbox" })}
          onToggle={onNetboxChange}
        />
      )}
      {showXOneToggle && (
        <ToolToggleItem
          enabled={enableXOne}
          icon={<ShieldCheck className={iconClassName(enableXOne)} />}
          label={t("questioninput.x_one", { defaultValue: "X-One" })}
          // showXOneToggle ensures onXOneChange is a function; the cast tells TS
          // what its narrowing already confirms.
          onToggle={onXOneChange as (enabled: boolean) => void}
        />
      )}
      {activePersonalSearch && (
        <>
          <ToolToggleItem
            disabled={activePersonalSearch.locked}
            enabled={activePersonalSearch.enabled}
            icon={<Mail className={iconClassName(activePersonalSearch.enabled)} />}
            label={personalSearchLabel}
            testId="personal-search-toggle"
            title={personalSearchLockedHint}
            onToggle={activePersonalSearch.onChange}
          />
          {(activePersonalSearch.enabled ||
            activePersonalSearch.locked ||
            activePersonalSearch.tainted) && (
            <PersonalSearchSourceRows
              lockedHint={personalSearchLockedHint}
              sources={activePersonalSearch.sources}
              tainted={activePersonalSearch.tainted}
            />
          )}
        </>
      )}
    </>
  )

  // Close the submenu/drawer and the parent menu so nothing stays mounted
  // behind the page navigation.
  const handleManagePlugins = onManagePlugins
    ? () => {
        setPluginsDrawerOpen(false)
        setToolsMenuOpen(false)
        onManagePlugins()
      }
    : undefined

  const pluginsPanel = hasPluginsMenu ? (
    <PluginsMenuPanel
      disabledPluginIds={disabledPluginIds}
      enablePlugins={enablePlugins}
      installed={pluginCatalog.installed}
      isError={pluginCatalog.isError}
      isPending={pluginCatalog.isPending}
      storeEnabled={pluginCatalog.storeEnabled}
      onManage={handleManagePlugins}
      onPluginDisabledChange={(pluginId, disabled) => onPluginDisabledChange?.(pluginId, disabled)}
      // hasPluginsMenu ensures onPluginsChange is a function.
      onPluginsChange={onPluginsChange as (enabled: boolean) => void}
    />
  ) : null

  const submenuCount = (count: number) =>
    count > 0 ? (
      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">({count})</span>
    ) : null
  const activeToolIndicators = useMemo((): ActiveToolIndicator[] => {
    const activeTools: ActiveToolIndicator[] = []

    if (enableAgent) {
      activeTools.push({
        id: "agent_mode",
        icon: <Telescope className="size-3.5" />,
      })
    }

    if (hasDeepToggle && enableDeep && !isDeepPlanLocked) {
      activeTools.push({
        id: "deep_mode",
        icon: <Sparkles className="size-3.5" />,
      })
    }

    if (showHighReasoning && enableHighReasoning) {
      activeTools.push({
        id: "high_reasoning",
        icon: <Orbit className="size-3.5" />,
      })
    }

    if (activeLibraryIds.length > 0) {
      activeTools.push({
        id: "libraries",
        icon: <Library className="size-3.5" />,
      })
    }

    if (!enableAgent && showWebSearchOption && enableWebSearch) {
      activeTools.push({
        id: "web_search",
        icon: <Globe className="size-3.5" />,
      })
    }

    if (!enableAgent && showM365Option && enableM365) {
      activeTools.push({
        id: "m365",
        icon: <M365Icon noText className="size-3.5" />,
      })
    }

    if (showServiceDeskOption && enableServiceDesk) {
      activeTools.push({
        id: "service_desk",
        icon: <LifeBuoy className="size-3.5" />,
      })
    }

    if (showNetboxOption && enableNetbox) {
      activeTools.push({
        id: "netbox",
        icon: <Server className="size-3.5" />,
      })
    }

    if (showXOneOption && enableXOne) {
      activeTools.push({
        id: "x_one",
        icon: <ShieldCheck className="size-3.5" />,
      })
    }

    if (hasTreeRetrievalToggle && enableTreeRetrieval) {
      activeTools.push({
        id: "tree_retrieval",
        icon: <Network className="size-3.5" />,
      })
    }

    if (hasPluginsMenu && activePluginCount > 0) {
      activeTools.push({
        id: "plugins",
        icon: <Plug className="size-3.5" />,
      })
    }

    return activeTools
  }, [
    enableAgent,
    hasDeepToggle,
    isDeepPlanLocked,
    enableDeep,
    t,
    showHighReasoning,
    enableHighReasoning,
    activeLibraryIds,
    showWebSearchOption,
    enableWebSearch,
    showM365Option,
    enableM365,
    showServiceDeskOption,
    enableServiceDesk,
    showNetboxOption,
    enableNetbox,
    showXOneOption,
    enableXOne,
    hasTreeRetrievalToggle,
    enableTreeRetrieval,
    hasPluginsMenu,
    activePluginCount,
  ])
  const visibleToolIndicators = activeToolIndicators.slice(0, 3)
  const toolOverflowCount = Math.max(0, activeToolIndicators.length - visibleToolIndicators.length)

  return (
    <>
      <DropdownMenu
        open={toolsMenuOpen}
        onOpenChange={(next) => {
          setToolsMenuOpen(next)
          // Closing Tools must also collapse the controlled trusted-sources
          // submenu, else it reopens already-expanded next time.
          if (!next) setTrustedSourcesMenuOpen(false)
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            aria-label={t("questioninput.tools", { defaultValue: "Tools" })}
            data-testid="chat-tools-menu"
            disabled={isStreaming}
            type="button"
            className={cn(
              buttonVariants({
                size: "md",
                variant: "ghost",
              }),
              "text-muted-foreground hover:text-foreground dark:hover:text-accent-foreground h-7 gap-1.5 px-2"
            )}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
          >
            <ToolsIcon className="shrink-0" />
            {visibleToolIndicators.length > 0 ? (
              <span
                aria-hidden="true"
                className="bg-primary/10 text-primary flex shrink-0 items-center gap-1 rounded px-2 py-[3px]"
              >
                {visibleToolIndicators.map((tool) => (
                  <span key={tool.id} className="flex items-center justify-center gap-1">
                    {tool.icon}
                    {tool.label ? (
                      <span className="text-[11px] font-medium">{tool.label}</span>
                    ) : null}
                  </span>
                ))}
                {toolOverflowCount > 0 && (
                  <span className="text-[10px] font-medium">+{toolOverflowCount}</span>
                )}
              </span>
            ) : (
              <span>{t("questioninput.tools", { defaultValue: "Tools" })}</span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px]" side="top">
          {translator && (
            <>
              <DropdownMenuItem
                data-testid="translator-tool-menu"
                onSelect={() => {
                  if (translator.selectedLanguages.length > 0) translator.onEnabledChange(true)
                  setTranslatorPanelOpen(true)
                }}
              >
                <Languages className={iconClassName(translator.enabled)} />
                <span>{t("translatorTool.title")}</span>
                {translator.enabled && (
                  <span className="text-muted-foreground ml-auto text-xs">
                    {translator.selectedLanguages.length}
                  </span>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {/* ===== AI MODES SECTION ===== */}
          {hasModesSection && (
            <>
              <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                {t("questioninput.modes_section", { defaultValue: "AI Mode" })}
              </DropdownMenuLabel>
              {hasAgentModeFeature && (
                <ToolToggleItem
                  enabled={enableAgent}
                  icon={<Telescope className={iconClassName(enableAgent)} />}
                  label={t("questioninput.agent_mode")}
                  onToggle={onAgentChange}
                />
              )}
              {hasDeepToggle && (
                <ToolToggleItem
                  badge={deepLockBadge ?? deepBetaBadge}
                  count={!isDeepPlanLocked && activeTodoCount > 0 ? activeTodoCount : undefined}
                  disabled={isDeepPlanLocked}
                  enabled={isDeepPlanLocked ? false : enableDeep}
                  label={t("questioninput.deep_mode", { defaultValue: "Deep" })}
                  title={deepLockHint}
                  icon={
                    isDeepPlanLocked ? (
                      <Lock className="text-muted-foreground size-4" />
                    ) : (
                      <Sparkles className={iconClassName(enableDeep)} />
                    )
                  }
                  onToggle={onDeepChange}
                />
              )}
              {showHighReasoning && onHighReasoningChange && (
                <ToolToggleItem
                  enabled={enableHighReasoning}
                  icon={<Orbit className={iconClassName(enableHighReasoning)} />}
                  label={t("questioninput.high_reasoning")}
                  onToggle={onHighReasoningChange}
                />
              )}
              {(hasSourcesSection || hasIntegrationsSection || hasUtilitiesSection) && (
                <DropdownMenuSeparator />
              )}
            </>
          )}

          {/* ===== KNOWLEDGE SOURCES SECTION ===== */}
          {hasSourcesSection && (
            <>
              <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                {t("questioninput.sources_section", { defaultValue: "Knowledge Sources" })}
              </DropdownMenuLabel>
              {shouldShowLibrariesInFooter &&
                (isMobile ? (
                  <DropdownMenuItem
                    className="my-0.5 gap-2"
                    onSelect={() => setLibrariesDrawerOpen(true)}
                  >
                    <Library
                      className={cn(
                        "size-4",
                        activeLibraryIds.length > 0 ? "text-primary" : "text-muted-foreground"
                      )}
                    />
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <span className="truncate">
                        {t("pages.common.libraries", { defaultValue: "Libraries" })}
                      </span>
                      {activeLibraryIds.length > 0 && (
                        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                          ({activeLibraryIds.length})
                        </span>
                      )}
                    </span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuSub open={librariesMenuOpen} onOpenChange={setLibrariesMenuOpen}>
                    <DropdownMenuSubTrigger>
                      <Library
                        className={cn(
                          "size-4",
                          activeLibraryIds.length > 0 ? "text-primary" : "text-muted-foreground"
                        )}
                      />
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span className="truncate">
                          {t("pages.common.libraries", { defaultValue: "Libraries" })}
                        </span>
                        {activeLibraryIds.length > 0 && (
                          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                            ({activeLibraryIds.length})
                          </span>
                        )}
                      </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-[420px] p-0">
                      <LibraryPicker
                        renderInline
                        activeLibraryIds={activeLibraryIds}
                        availableLibraries={availableLibraries}
                        onActivationChange={setActiveLibraryIds}
                        onClose={() => setLibrariesMenuOpen(false)}
                      />
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              {hasTreeRetrievalToggle && shouldShowLibrariesInFooter && (
                <ToolToggleItem
                  badge={treeRetrievalBetaBadge}
                  enabled={enableTreeRetrieval}
                  icon={<Network className={iconClassName(enableTreeRetrieval)} />}
                  label={t("questioninput.tree_retrieval", { defaultValue: "Tree retrieval" })}
                  // hasTreeRetrievalToggle ensures onTreeRetrievalChange is a function;
                  // the cast tells TS what its narrowing already confirms.
                  onToggle={onTreeRetrievalChange as (enabled: boolean) => void}
                />
              )}
              {!enableAgent && (
                <>
                  {showWebSearchOption && (
                    <ToolToggleItem
                      enabled={enableWebSearch}
                      icon={<Globe className={iconClassName(enableWebSearch)} />}
                      label={t("questioninput.web_search")}
                      onToggle={onWebSearchChange}
                    />
                  )}
                  {showWebSearchOption && enableWebSearch && isMobile && (
                    <DropdownMenuItem
                      className="my-0.5 gap-2"
                      onSelect={() => setTrustedSourcesDrawerOpen(true)}
                    >
                      <ShieldCheck
                        className={cn(
                          "size-4",
                          selectedUrls.length > 0 ? "text-primary" : "text-muted-foreground"
                        )}
                      />
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span className="truncate">
                          {t("sources.trusted_sources", { defaultValue: "Trusted sources" })}
                        </span>
                        {selectedUrls.length > 0 && (
                          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                            ({selectedUrls.length})
                          </span>
                        )}
                      </span>
                    </DropdownMenuItem>
                  )}
                  {showWebSearchOption && enableWebSearch && !isMobile && (
                    <DropdownMenuSub
                      open={trustedSourcesMenuOpen}
                      onOpenChange={setTrustedSourcesMenuOpen}
                    >
                      <DropdownMenuSubTrigger>
                        <ShieldCheck
                          className={cn(
                            "size-4",
                            selectedUrls.length > 0 ? "text-primary" : "text-muted-foreground"
                          )}
                        />
                        <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                          <span className="truncate">
                            {t("sources.trusted_sources", { defaultValue: "Trusted sources" })}
                          </span>
                          {selectedUrls.length > 0 && (
                            <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                              ({selectedUrls.length})
                            </span>
                          )}
                        </span>
                      </DropdownMenuSubTrigger>
                      {/* Plain-button panel inside a stopPropagation wrapper: each
                          pick toggles state without Radix dismissing the submenu,
                          so multiple sources can be selected in one pass. */}
                      <DropdownMenuSubContent className="w-[280px] p-0">
                        <div
                          className="max-h-[400px] overflow-y-auto py-1"
                          role="presentation"
                          onClick={(e) => e.stopPropagation()}
                          // Keep selections from closing the menu, but let Escape
                          // bubble to Radix so it can still close the submenu.
                          onKeyDown={(e) => {
                            if (e.key !== "Escape") e.stopPropagation()
                          }}
                        >
                          <TrustedSourcesPanel
                            companySources={companySources}
                            hasSources={trustedSources.length > 0}
                            isAllSelected={isAllSelected}
                            personalSources={personalSources}
                            selectedUrls={selectedUrls}
                            setTrustedMode={setTrustedMode}
                            toggleAll={toggleAll}
                            toggleSource={toggleSource}
                            trustedMode={trustedMode}
                            onManage={() => {
                              // Close both the submenu and the parent tools menu
                              // so it isn't left mounted behind the dialog.
                              setTrustedSourcesMenuOpen(false)
                              setToolsMenuOpen(false)
                              setShowTrustedSourcesDialog(true)
                            }}
                          />
                        </div>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                </>
              )}
              {(hasIntegrationsSection || hasUtilitiesSection) && <DropdownMenuSeparator />}
            </>
          )}

          {/* ===== INTEGRATIONS SECTION (Tools + Plugins submenus) ===== */}
          {hasIntegrationsSection && (
            <>
              <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                {t("questioninput.integrations_section", { defaultValue: "Integrations" })}
              </DropdownMenuLabel>
              {showToolsSubmenu &&
                (isMobile ? (
                  <DropdownMenuItem
                    className="my-0.5 gap-2"
                    data-testid="chat-tools-submenu"
                    onSelect={() => setToolsDrawerOpen(true)}
                  >
                    <Wrench className={iconClassName(activeToolCount > 0)} />
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <span className="truncate">
                        {t("questioninput.tools_submenu", { defaultValue: "Tools" })}
                      </span>
                      {submenuCount(activeToolCount)}
                    </span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid="chat-tools-submenu">
                      <Wrench className={iconClassName(activeToolCount > 0)} />
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span className="truncate">
                          {t("questioninput.tools_submenu", { defaultValue: "Tools" })}
                        </span>
                        {submenuCount(activeToolCount)}
                      </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="min-w-[220px]">
                      {integrationToggleItems}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              {hasPluginsMenu &&
                (isMobile ? (
                  <DropdownMenuItem
                    className="my-0.5 gap-2"
                    data-testid="chat-plugins-submenu"
                    onSelect={() => setPluginsDrawerOpen(true)}
                  >
                    <Plug className={iconClassName(activePluginCount > 0)} />
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <span className="truncate">
                        {t("questioninput.plugins_submenu", { defaultValue: "Plugins" })}
                      </span>
                      {submenuCount(activePluginCount)}
                    </span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid="chat-plugins-submenu">
                      <Plug className={iconClassName(activePluginCount > 0)} />
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span className="truncate">
                          {t("questioninput.plugins_submenu", { defaultValue: "Plugins" })}
                        </span>
                        {submenuCount(activePluginCount)}
                      </span>
                    </DropdownMenuSubTrigger>
                    {/* Plain-button panel inside a stopPropagation wrapper (same
                        pattern as trusted sources) so several plugins can be
                        switched in one pass without Radix closing the submenu. */}
                    <DropdownMenuSubContent className="w-[300px] p-0">
                      <div
                        className="max-h-[400px] overflow-y-auto px-1"
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key !== "Escape") e.stopPropagation()
                        }}
                      >
                        {pluginsPanel}
                      </div>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              {hasUtilitiesSection && <DropdownMenuSeparator />}
            </>
          )}

          {/* ===== UTILITIES SECTION ===== */}
          {hasUtilitiesSection && (
            <>
              <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                {t("questioninput.utilities_section", { defaultValue: "Utilities" })}
              </DropdownMenuLabel>
              {isPromptsFeatureEnabled && isMobile && (
                <DropdownMenuItem
                  className="my-0.5 gap-2"
                  onSelect={() => setPromptsDrawerOpen(true)}
                >
                  <BookOpen className="text-muted-foreground size-4" />
                  <span className="min-w-0 flex-1 truncate">
                    {t("prompts.title", { defaultValue: "Prompt templates" })}
                  </span>
                </DropdownMenuItem>
              )}
              {isPromptsFeatureEnabled && !isMobile && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <BookOpen className="text-muted-foreground size-4" />
                    <span className="min-w-0 flex-1 truncate">
                      {t("prompts.title", { defaultValue: "Prompt templates" })}
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-[500px] overflow-y-auto">
                    {isPromptsPending ? (
                      <DropdownMenuItem disabled>
                        {t("prompts.loading_prompts", { defaultValue: "Loading prompts..." })}
                      </DropdownMenuItem>
                    ) : (
                      <>
                        {personalPrompts.length > 0 && (
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              {t("prompts.personal", { defaultValue: "Personal" })}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="max-h-[500px] max-w-[250px] overflow-y-auto">
                              {personalPrompts.map((prompt) => (
                                <DropdownMenuItem
                                  key={prompt.id}
                                  onSelect={() => onPromptClicked?.(prompt)}
                                >
                                  <span className="truncate">{prompt.title}</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        )}
                        {companyPrompts.length > 0 && (
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              {t("prompts.company", { defaultValue: "Company" })}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="max-h-[500px] max-w-[250px] overflow-y-auto">
                              {companyPrompts.map((prompt) => (
                                <DropdownMenuItem
                                  key={prompt.id}
                                  onSelect={() => onPromptClicked?.(prompt)}
                                >
                                  <span className="truncate">{prompt.title}</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        )}
                        {prompts.length === 0 && (
                          <DropdownMenuItem disabled>
                            {t("prompts.no_prompts_available", {
                              defaultValue: "No prompts available",
                            })}
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    <DropdownMenuItem onSelect={() => setShowPromptLibrariesDialog(true)}>
                      {t("prompts.manage", { defaultValue: "Manage" })}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {showVoiceMenu && onStartVoiceMode && (
                <DropdownMenuItem
                  className="gap-2"
                  disabled={isStreaming}
                  onSelect={() => onStartVoiceMode()}
                >
                  <AudioLines className="text-muted-foreground size-4" />
                  <span className="min-w-0 flex-1 truncate">
                    {t("questioninput.start_voice_mode")}
                  </span>
                  <span className="ml-auto size-4" />
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {translator &&
        (isMobile ? (
          <Drawer open={translatorPanelOpen} onOpenChange={setTranslatorPanelOpen}>
            <DrawerContent onCloseAutoFocus={restoreTranslatorFocus}>
              <DrawerHeader>
                <DrawerTitle>{t("translatorTool.title")}</DrawerTitle>
                <DrawerDescription className="sr-only">
                  {t("translatorTool.sourceHint")}
                </DrawerDescription>
              </DrawerHeader>
              <div className="max-h-[70dvh] overflow-y-auto px-4 pb-6">
                <TranslatorToolPanel
                  control={translator}
                  onClose={() => setTranslatorPanelOpen(false)}
                />
              </div>
            </DrawerContent>
          </Drawer>
        ) : (
          <Dialog open={translatorPanelOpen} onOpenChange={setTranslatorPanelOpen}>
            <DialogContent onCloseAutoFocus={restoreTranslatorFocus}>
              <DialogHeader>
                <DialogTitle>{t("translatorTool.title")}</DialogTitle>
                <DialogDescription className="sr-only">
                  {t("translatorTool.sourceHint")}
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[70dvh] overflow-y-auto">
                <TranslatorToolPanel
                  control={translator}
                  onClose={() => setTranslatorPanelOpen(false)}
                />
              </div>
            </DialogContent>
          </Dialog>
        ))}

      {translator?.enabled && (
        <button
          ref={translatorIndicatorRef}
          aria-expanded={translatorPanelOpen}
          aria-haspopup="dialog"
          aria-label={`${t("translatorTool.title")}: ${translator.selectedLanguages.join(", ")}`}
          className="focus-visible:ring-ring inline-flex min-h-10 shrink-0 items-center rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="translator-active-indicator"
          disabled={isStreaming}
          title={`${t("translatorTool.title")}: ${translator.selectedLanguages.join(", ")}`}
          type="button"
          onClick={() => setTranslatorPanelOpen(true)}
          onMouseDown={(event) => event.preventDefault()}
        >
          <NodePill
            active
            className="max-w-44 gap-2"
            icon={LanguagesSolid}
            size="sm"
            tone="primary"
          >
            <span className="min-w-0 truncate">{t("translatorTool.title")}</span>
            <span className="shrink-0 tabular-nums">{translator.selectedLanguages.length}</span>
          </NodePill>
        </button>
      )}

      {/* Agent Mode indicator pill - shown when agent mode is active */}
      {enableAgent && (
        <button
          type="button"
          className={cn(
            "border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors"
          )}
          onClick={() => onAgentChange(false)}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Telescope className="size-3.5" />
          <span>{t("questioninput.agent_mode")}</span>
          <X className="size-3 opacity-60" />
        </button>
      )}

      {hasPluginsMenu && <PluginCatalogBridge onChange={setPluginCatalog} />}
      <TrustedSourcesDialog
        open={showTrustedSourcesDialog}
        onOpenChange={setShowTrustedSourcesDialog}
      />
      <PromptLibrariesDialog
        open={showPromptLibrariesDialog}
        onOpenChange={setShowPromptLibrariesDialog}
      />

      {isMobile && shouldShowLibrariesInFooter && (
        <Drawer open={librariesDrawerOpen} onOpenChange={setLibrariesDrawerOpen}>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <div className="flex items-center gap-2">
                <DrawerBackButton
                  label={t("common.back", { defaultValue: "Back" })}
                  onClick={backToToolsMenu(() => setLibrariesDrawerOpen(false))}
                />
                <DrawerTitle>
                  {t("pages.common.libraries", { defaultValue: "Libraries" })}
                </DrawerTitle>
              </div>
            </DrawerHeader>
            <div className="max-h-[75vh] overflow-y-auto px-2 pb-4">
              <LibraryPicker
                renderInline
                activeLibraryIds={activeLibraryIds}
                availableLibraries={availableLibraries}
                onActivationChange={setActiveLibraryIds}
                onClose={() => setLibrariesDrawerOpen(false)}
              />
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {isMobile && isPromptsFeatureEnabled && (
        <Drawer open={promptsDrawerOpen} onOpenChange={setPromptsDrawerOpen}>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <div className="flex items-center gap-2">
                <DrawerBackButton
                  label={t("common.back", { defaultValue: "Back" })}
                  onClick={backToToolsMenu(() => setPromptsDrawerOpen(false))}
                />
                <DrawerTitle>
                  {t("prompts.title", { defaultValue: "Prompt templates" })}
                </DrawerTitle>
              </div>
            </DrawerHeader>
            <div className="max-h-[75vh] overflow-y-auto pb-4">
              {isPromptsPending && (
                <div className="text-muted-foreground px-4 py-2 text-sm">
                  {t("prompts.loading_prompts", { defaultValue: "Loading prompts..." })}
                </div>
              )}
              {!isPromptsPending && prompts.length === 0 && (
                <div className="text-muted-foreground px-4 py-2 text-sm">
                  {t("prompts.no_prompts_available", { defaultValue: "No prompts available" })}
                </div>
              )}
              {!isPromptsPending && prompts.length > 0 && (
                <>
                  {personalPrompts.length > 0 && (
                    <>
                      <div className="text-muted-foreground px-4 pt-2 pb-1 text-xs font-medium uppercase">
                        {t("prompts.personal", { defaultValue: "Personal" })}
                      </div>
                      {personalPrompts.map((prompt) => (
                        <button
                          key={prompt.id}
                          className="hover:bg-accent w-full px-4 py-2 text-left text-sm"
                          type="button"
                          onClick={() => {
                            setPromptsDrawerOpen(false)
                            onPromptClicked?.(prompt)
                          }}
                        >
                          <span className="truncate">{prompt.title}</span>
                        </button>
                      ))}
                    </>
                  )}
                  {companyPrompts.length > 0 && (
                    <>
                      <div className="text-muted-foreground px-4 pt-3 pb-1 text-xs font-medium uppercase">
                        {t("prompts.company", { defaultValue: "Company" })}
                      </div>
                      {companyPrompts.map((prompt) => (
                        <button
                          key={prompt.id}
                          className="hover:bg-accent w-full px-4 py-2 text-left text-sm"
                          type="button"
                          onClick={() => {
                            setPromptsDrawerOpen(false)
                            onPromptClicked?.(prompt)
                          }}
                        >
                          <span className="truncate">{prompt.title}</span>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
              <div className="border-border mt-2 border-t px-2 pt-2">
                <button
                  className="hover:bg-accent w-full rounded px-2 py-2 text-left text-sm"
                  type="button"
                  onClick={() => {
                    setPromptsDrawerOpen(false)
                    setShowPromptLibrariesDialog(true)
                  }}
                >
                  {t("prompts.manage", { defaultValue: "Manage" })}
                </button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {isMobile && showToolsSubmenu && (
        <Drawer open={toolsDrawerOpen} onOpenChange={setToolsDrawerOpen}>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <div className="flex items-center gap-2">
                <DrawerBackButton
                  label={t("common.back", { defaultValue: "Back" })}
                  onClick={backToToolsMenu(() => setToolsDrawerOpen(false))}
                />
                <DrawerTitle>
                  {t("questioninput.tools_submenu", { defaultValue: "Tools" })}
                </DrawerTitle>
              </div>
            </DrawerHeader>
            <div className="max-h-[75vh] overflow-y-auto px-2 pb-4">
              {!enableAgent && showM365Option && (
                <MenuToggleRow
                  checked={enableM365}
                  icon={<M365Icon noText className={iconClassName(enableM365)} />}
                  label={t("questioninput.m365_search")}
                  onToggle={onM365Change}
                />
              )}
              {showServiceDeskOption && (
                <MenuToggleRow
                  checked={enableServiceDesk}
                  icon={<LifeBuoy className={iconClassName(enableServiceDesk)} />}
                  label={t("questioninput.service_desk", { defaultValue: "ServiceDesk" })}
                  onToggle={onServiceDeskChange}
                />
              )}
              {showNetboxOption && (
                <MenuToggleRow
                  checked={enableNetbox}
                  icon={<Server className={iconClassName(enableNetbox)} />}
                  label={t("questioninput.netbox", { defaultValue: "Netbox" })}
                  onToggle={onNetboxChange}
                />
              )}
              {showXOneToggle && (
                <MenuToggleRow
                  checked={enableXOne}
                  icon={<ShieldCheck className={iconClassName(enableXOne)} />}
                  label={t("questioninput.x_one", { defaultValue: "X-One" })}
                  onToggle={onXOneChange as (enabled: boolean) => void}
                />
              )}
              {activePersonalSearch && (
                <>
                  <MenuToggleRow
                    checked={activePersonalSearch.enabled}
                    description={personalSearchLockedHint}
                    disabled={activePersonalSearch.locked}
                    icon={<Mail className={iconClassName(activePersonalSearch.enabled)} />}
                    label={personalSearchLabel}
                    testId="personal-search-toggle"
                    onToggle={activePersonalSearch.onChange}
                  />
                  {(activePersonalSearch.enabled ||
                    activePersonalSearch.locked ||
                    activePersonalSearch.tainted) && (
                    <PersonalSearchSourceRows
                      lockedHint={personalSearchLockedHint}
                      sources={activePersonalSearch.sources}
                      tainted={activePersonalSearch.tainted}
                    />
                  )}
                </>
              )}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {isMobile && hasPluginsMenu && (
        <Drawer open={pluginsDrawerOpen} onOpenChange={setPluginsDrawerOpen}>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <div className="flex items-center gap-2">
                <DrawerBackButton
                  label={t("common.back", { defaultValue: "Back" })}
                  onClick={backToToolsMenu(() => setPluginsDrawerOpen(false))}
                />
                <DrawerTitle>
                  {t("questioninput.plugins_submenu", { defaultValue: "Plugins" })}
                </DrawerTitle>
              </div>
            </DrawerHeader>
            <div className="max-h-[75vh] overflow-y-auto px-2 pb-4">{pluginsPanel}</div>
          </DrawerContent>
        </Drawer>
      )}

      {isMobile && showWebSearchOption && enableWebSearch && (
        <Drawer open={trustedSourcesDrawerOpen} onOpenChange={setTrustedSourcesDrawerOpen}>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <div className="flex items-center gap-2">
                <DrawerBackButton
                  label={t("common.back", { defaultValue: "Back" })}
                  onClick={backToToolsMenu(() => setTrustedSourcesDrawerOpen(false))}
                />
                <DrawerTitle>
                  {t("sources.trusted_sources", { defaultValue: "Trusted sources" })}
                </DrawerTitle>
              </div>
            </DrawerHeader>
            <div className="max-h-[75vh] overflow-y-auto pb-4">
              <TrustedSourcesPanel
                companySources={companySources}
                hasSources={trustedSources.length > 0}
                isAllSelected={isAllSelected}
                personalSources={personalSources}
                selectedUrls={selectedUrls}
                setTrustedMode={setTrustedMode}
                toggleAll={toggleAll}
                toggleSource={toggleSource}
                trustedMode={trustedMode}
                onManage={() => {
                  setTrustedSourcesDrawerOpen(false)
                  setShowTrustedSourcesDialog(true)
                }}
              />
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </>
  )
}
